/**
 * DOG OWNED — Quick Add.
 *
 * One controller for every normal product card, wherever it renders (collections,
 * search, recommendations). Eligibility is decided server-side in
 * snippets/quick-add-action.liquid from native product truth; this file only acts
 * on the control that was rendered.
 *
 * It never invents a cart result: every add goes through window.CartPack, which
 * reads /cart.js before and after and reports the true per-variant delta — the
 * HTTP status is not trusted. Success is announced only after that reconciliation.
 *
 * The variant chooser is a native <dialog> opened with showModal(): the focus
 * trap, Escape-to-close, inert background and focus restoration are the browser's.
 * Its content is built from Shopify's own product JSON (/products/{handle}.js) —
 * no product data is copied into this file product by product.
 */
window.QuickAdd = (function () {
  'use strict';

  var MONEY = '${{amount}}';
  var S = {};
  var dialog, panel, live, el = {};
  var current = { product: null, selected: [], variant: null };
  var productCache = {}; // handle -> product JSON (a UI cache; the add still reconciles)

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Canonical Shopify money formatter, driven by the shop's own money_format. */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || MONEY;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var pounds = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var pennies = parts[1] ? decimal + parts[1] : '';
      return pounds + pennies;
    }
    var match = fmt.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  /* --- Cart badge, from authoritative cart data only --- */
  function refreshBadge() {
    return fetch(routeRoot() + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        var badge = document.querySelector('[data-cart-count]');
        var link = document.querySelector('[data-cart-link]');
        if (badge) { badge.textContent = count; badge.hidden = count === 0; }
        if (link) {
          var tmpl = count === 1 ? link.getAttribute('data-cart-label-one') : link.getAttribute('data-cart-label-other');
          if (tmpl && tmpl.indexOf('translation missing') === -1) link.setAttribute('aria-label', tmpl.replace('[count]', count));
        }
        return count;
      })
      .catch(function () { /* leave the server-rendered badge as-is */ });
  }

  function announce(node, message) {
    if (!node) return;
    node.textContent = '';
    // A tick so assistive tech reliably re-announces an identical message.
    window.setTimeout(function () { node.textContent = message; }, 60);
  }

  /* ---------------------------------------------------------------- Simple add */
  function simpleAdd(button) {
    if (button.getAttribute('aria-busy') === 'true') return; // in-flight guard
    if (!window.CartPack) { window.location.href = button.getAttribute('data-product-url'); return; }

    var variantId = button.getAttribute('data-variant-id');
    var title = button.getAttribute('data-product-title') || '';
    var label = button.querySelector('[data-quick-add-label]');
    var idle = button.getAttribute('data-label-idle');
    var pending = button.getAttribute('data-label-pending');

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (label && pending) label.textContent = pending;

    window.CartPack.add([{ variantId: variantId, quantity: 1 }]).then(function (res) {
      var added = res && res.results && res.results[0] && res.results[0].delta > 0;
      if (res && res.outcome === 'busy') { restore(); return; }
      if (added) {
        refreshBadge();
        announce(live, (S.added || '@@TITLE@@').replace('@@TITLE@@', title));
        succeed();
      } else {
        announce(live, S.error_generic || '');
        restore();
        flashError(button);
      }
    }).catch(function () { announce(live, S.error_generic || ''); restore(); flashError(button); });

    function succeed() {
      button.removeAttribute('aria-busy');
      button.classList.add('quick-add--added');
      if (label) label.textContent = S.added_generic || idle;
      window.setTimeout(function () {
        button.classList.remove('quick-add--added');
        button.disabled = false;
        if (label && idle) label.textContent = idle;
      }, 2200);
    }
    function restore() {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (label && idle) label.textContent = idle;
    }
  }

  function flashError(button) {
    button.classList.add('quick-add--error');
    window.setTimeout(function () { button.classList.remove('quick-add--error'); }, 2400);
  }

  /* --------------------------------------------------------------- Variant chooser */
  function openChooser(button) {
    var handle = button.getAttribute('data-product-handle');
    if (!handle || !dialog) { window.location.href = button.getAttribute('data-product-url'); return; }

    // Re-opening the same product reuses the cached JSON. Availability here is
    // only a UI hint — the add always reconciles against /cart.js, so a variant
    // that sold out since it was cached is caught at submission, not trusted.
    if (productCache[handle]) { populate(productCache[handle], button); openDialog(); return; }

    button.setAttribute('aria-busy', 'true');
    fetch(routeRoot() + 'products/' + encodeURIComponent(handle) + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error('load'); return r.json(); })
      .then(function (product) {
        button.removeAttribute('aria-busy');
        productCache[handle] = product;
        populate(product, button);
        openDialog();
      })
      .catch(function () {
        button.removeAttribute('aria-busy');
        // If the data can't be loaded, fall back to the honest destination.
        window.location.href = button.getAttribute('data-product-url');
      });
  }

  function populate(product, button) {
    current.product = product;
    current.selected = product.options.map(function () { return null; });
    current.variant = null;

    // Preselect only options that have a single value (no real choice). Multi-value
    // options — size, colour — are left unchosen so the customer consciously picks
    // and Add stays disabled until the selection is a real, available variant.
    product.options.forEach(function (name, i) {
      var values = optionValues(product, i);
      if (values.length === 1) current.selected[i] = values[0];
    });

    // Title, vendor, view link
    el.title.textContent = product.title;
    if (product.vendor) { el.vendor.textContent = product.vendor; el.vendor.hidden = false; } else { el.vendor.hidden = true; }
    el.viewLink.setAttribute('href', button.getAttribute('data-product-url') || (routeRoot() + 'products/' + product.handle));

    // Fit note + size guide (Dog Owned intelligence carried on the trigger)
    var fitNote = button.getAttribute('data-fit-note');
    if (fitNote) { el.fitnote.textContent = fitNote; el.fitnote.hidden = false; } else { el.fitnote.hidden = true; }
    var sizeGuide = button.getAttribute('data-size-guide-url');
    var hasSizeOption = product.options.some(function (n) { return /size|fit/i.test(n); });
    if (sizeGuide && hasSizeOption) { el.sizeguide.setAttribute('href', sizeGuide); el.sizeguide.hidden = false; } else { el.sizeguide.hidden = true; }

    // Quantity + add control reset to a clean state on every open.
    el.quantity.value = '1';
    el.add.removeAttribute('aria-busy');
    var addLbl = el.add.querySelector('[data-qa-add-label]');
    if (addLbl && S.add) addLbl.textContent = S.add;
    hideError();

    buildOptions(product);
    updateVariant();
  }

  function optionValues(product, index) {
    var seen = [], values = [];
    product.variants.forEach(function (v) {
      var val = v.options[index];
      if (seen.indexOf(val) === -1) { seen.push(val); values.push(val); }
    });
    return values; // preserves the merchant's option ordering
  }

  function buildOptions(product) {
    el.options.innerHTML = '';
    // has_only_default_variant shows as a single option "Title" = "Default Title".
    if (product.options.length === 1 && product.variants.length === 1 && product.variants[0].options[0] === 'Default Title') {
      current.selected[0] = 'Default Title';
      return;
    }
    product.options.forEach(function (name, i) {
      var fieldset = document.createElement('fieldset');
      fieldset.className = 'qa-option';
      fieldset.setAttribute('data-qa-option', i);

      var legend = document.createElement('legend');
      legend.className = 'qa-option__legend';
      legend.textContent = name;
      fieldset.appendChild(legend);

      var wrap = document.createElement('div');
      wrap.className = 'qa-option__values';

      optionValues(product, i).forEach(function (value, j) {
        var id = 'qa-opt-' + i + '-' + j;
        var input = document.createElement('input');
        input.className = 'qa-option__input visually-hidden';
        input.type = 'radio';
        input.name = 'qa-option-' + i;
        input.id = id;
        input.value = value;
        if (current.selected[i] === value) input.checked = true;
        input.addEventListener('change', function () { current.selected[i] = value; updateVariant(); });

        var label = document.createElement('label');
        label.className = 'qa-option__label';
        label.setAttribute('for', id);
        label.textContent = value;

        wrap.appendChild(input);
        wrap.appendChild(label);
      });

      fieldset.appendChild(wrap);
      el.options.appendChild(fieldset);
    });
  }

  /* Would choosing `value` at `index`, with the other current selections, reach
     an available variant? Drives the unavailable-value state (text + semantics). */
  function valueReachable(index, value) {
    return current.product.variants.some(function (v) {
      if (!v.available) return false;
      if (v.options[index] !== value) return false;
      for (var i = 0; i < current.selected.length; i++) {
        if (i !== index && current.selected[i] && v.options[i] !== current.selected[i]) return false;
      }
      return true;
    });
  }

  function findVariant() {
    if (current.selected.indexOf(null) > -1) return null; // incomplete
    return current.product.variants.filter(function (v) {
      return v.options.every(function (val, i) { return val === current.selected[i]; });
    })[0] || null;
  }

  function updateVariant() {
    // Mark unreachable option values (never colour alone — disabled + text).
    el.options.querySelectorAll('[data-qa-option]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-qa-option'), 10);
      group.querySelectorAll('.qa-option__input').forEach(function (input) {
        var reachable = valueReachable(idx, input.value);
        input.disabled = !reachable;
        var label = group.querySelector('label[for="' + input.id + '"]');
        if (label) {
          label.classList.toggle('qa-option__label--unavailable', !reachable);
          var note = label.querySelector('.qa-option__x');
          if (!reachable && !note) {
            note = document.createElement('span');
            note.className = 'qa-option__x visually-hidden';
            note.textContent = ' (' + (S.unavailable || 'unavailable') + ')';
            label.appendChild(note);
          } else if (reachable && note) {
            label.removeChild(note);
          }
        }
      });
    });

    var variant = findVariant();
    current.variant = variant;

    // Image
    var img = variant && variant.featured_image ? variant.featured_image.src
      : (current.product.featured_image || (current.product.images && current.product.images[0]));
    if (img) { el.image.src = withWidth(img, 900); el.image.alt = current.product.title; el.image.hidden = false; el.fallback.hidden = true; }
    else { el.image.hidden = true; el.fallback.hidden = false; }

    // Price / compare-at
    var priceCents = variant ? variant.price : current.product.price;
    el.price.textContent = formatMoney(priceCents, MONEY);
    var compare = variant ? variant.compare_at_price : current.product.compare_at_price;
    if (compare && compare > priceCents) { el.compare.textContent = formatMoney(compare, MONEY); el.compare.hidden = false; }
    else { el.compare.hidden = true; }

    // SKU
    if (variant && variant.sku) { el.sku.textContent = (S.sku_label || 'SKU @@SKU@@').replace('@@SKU@@', variant.sku); el.sku.hidden = false; }
    else { el.sku.hidden = true; }

    // Availability + Add state
    var addable = !!(variant && variant.available);
    el.add.disabled = !addable;
    if (variant && !variant.available) { el.availability.textContent = S.sold_out || ''; }
    else if (!variant) { el.availability.textContent = S.choose_prompt || ''; }
    else { el.availability.textContent = S.in_stock || ''; }
  }

  function withWidth(src, width) {
    // Shopify CDN image; add a width param without disturbing an existing one.
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) { return src; }
  }

  function dialogAdd(e) {
    e.preventDefault();
    if (!current.variant || !current.variant.available) return;
    if (el.add.getAttribute('aria-busy') === 'true') return;
    if (!window.CartPack) { window.location.href = el.viewLink.getAttribute('href'); return; }

    var qty = parseInt(el.quantity.value, 10);
    if (!qty || qty < 1) qty = 1;
    var variantId = current.variant.id;
    var addLabel = el.add.querySelector('[data-qa-add-label]');
    var idle = addLabel ? addLabel.textContent : '';

    el.add.setAttribute('aria-busy', 'true');
    el.add.disabled = true;
    if (addLabel) addLabel.textContent = S.adding || idle;
    hideError();

    window.CartPack.add([{ variantId: variantId, quantity: qty }]).then(function (res) {
      var addedDelta = res && res.results && res.results[0] ? res.results[0].delta : 0;
      el.add.removeAttribute('aria-busy');
      if (res && res.outcome === 'busy') { el.add.disabled = false; if (addLabel) addLabel.textContent = idle; return; }
      if (addedDelta > 0) {
        refreshBadge();
        announce(live, (S.added || '@@TITLE@@').replace('@@TITLE@@', current.product.title));
        closeDialog(); // reconciled success → close; native returns focus to the card
      } else {
        el.add.disabled = false;
        if (addLabel) addLabel.textContent = idle;
        // A network/endpoint error is a "try again"; a clean zero-delta is a
        // genuine unavailability discovered at add time.
        showError(res && res.outcome === 'error' ? (S.error_generic || '') : (S.error_unavailable || S.error_generic || ''));
        updateVariant();
      }
    }).catch(function () {
      el.add.removeAttribute('aria-busy');
      el.add.disabled = false;
      if (addLabel) addLabel.textContent = idle;
      showError(S.error_generic || '');
    });
  }

  function showError(msg) { el.error.textContent = msg; el.error.hidden = false; }
  function hideError() { el.error.textContent = ''; el.error.hidden = true; }

  /* --------------------------------------------------------------- Dialog open/close */
  function openDialog() {
    document.documentElement.classList.add('qa-scroll-lock');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }
  function closeDialog() {
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
  }

  /* ----------------------------------------------------------------------- Wiring */
  function loadConfig() {
    var node = document.querySelector('[data-qa-config]');
    if (!node) return;
    try {
      var cfg = JSON.parse(node.textContent);
      MONEY = cfg.money_format || MONEY;
      S = cfg.strings || {};
    } catch (e) { /* defaults stand */ }
  }

  function cacheDialog() {
    dialog = document.querySelector('[data-qa-dialog]');
    live = document.querySelector('[data-qa-live]');
    if (!dialog) return;
    panel = dialog.querySelector('[data-qa-panel]');
    el.title = dialog.querySelector('[data-qa-title]');
    el.vendor = dialog.querySelector('[data-qa-vendor]');
    el.image = dialog.querySelector('[data-qa-image]');
    el.fallback = dialog.querySelector('[data-qa-fallback]');
    el.price = dialog.querySelector('[data-qa-price]');
    el.compare = dialog.querySelector('[data-qa-compare]');
    el.availability = dialog.querySelector('[data-qa-availability]');
    el.options = dialog.querySelector('[data-qa-options]');
    el.fitnote = dialog.querySelector('[data-qa-fitnote]');
    el.sizeguide = dialog.querySelector('[data-qa-sizeguide]');
    el.quantity = dialog.querySelector('[data-qa-quantity]');
    el.sku = dialog.querySelector('[data-qa-sku]');
    el.error = dialog.querySelector('[data-qa-error]');
    el.add = dialog.querySelector('[data-qa-add]');
    el.viewLink = dialog.querySelector('[data-qa-viewlink]');
    el.form = dialog.querySelector('[data-qa-form]');
    el.close = dialog.querySelector('[data-qa-close]');

    if (el.form) el.form.addEventListener('submit', dialogAdd);
    if (el.close) el.close.addEventListener('click', closeDialog);
    // Backdrop click closes (a click on the dialog itself, not its panel).
    dialog.addEventListener('click', function (e) { if (e.target === dialog) closeDialog(); });
    // Native Escape/close: unlock scroll. Focus returns to the trigger natively.
    dialog.addEventListener('close', function () { document.documentElement.classList.remove('qa-scroll-lock'); });
  }

  function onClick(e) {
    var control = e.target.closest('[data-quick-add]');
    if (!control) return;
    var mode = control.getAttribute('data-quick-add');
    if (mode === 'view') return; // a plain link — let it navigate
    e.preventDefault();
    if (mode === 'add') simpleAdd(control);
    else if (mode === 'choose') openChooser(control);
  }

  function init() {
    if (window.__quickAddInited) return; // never bind twice (hot reload / double include)
    // Nothing to do on a page with neither a Quick Add control nor the chooser.
    if (!document.querySelector('[data-quick-add]') && !document.querySelector('[data-qa-dialog]')) return;
    window.__quickAddInited = true;
    loadConfig();
    cacheDialog();
    // One delegated listener handles every control, including any added later by
    // a theme-editor section reload — so listeners never multiply.
    document.addEventListener('click', onClick);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  return { refreshBadge: refreshBadge };
})();
